import {
  DragEvent,
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  TPopulatedForm,
  useApplicationFormSteps,
  useNameOfServiceField,
} from "../../hooks/applications.hooks";
import {
  useFieldSequenceAdminView,
  useSaveFieldSequence,
} from "../../hooks/field-sequence.hooks";
import { branchFeatureFlags } from "../../config/branchFeatureFlags";
import { sortByDocumentDisplayOrder } from "../../config/phaseConstants";
import Select from "../CommonComponents/Select/Select";
import { RenderField } from "../RenderField/RenderField";
import { SuccessToast } from "../toastMessage";
import {
  buildOrder,
  defaultFieldOrder,
  moveItem,
  newSpacer,
  orderToSaved,
  placeStepItems,
  stepItems,
  TOrderItem,
  TSavedItem,
} from "./fieldSequenceOrder";

/** The service the screen opens on, when the municipality has it. */
const DEFAULT_SERVICE_NAME = "building development";

/** Fields the applicant form only shows after a Yes to "Is there a construction?". */
const CONSTRUCTION_ONLY_UIDS = ["construction_area", "built_up_area"];

/**
 * The Admin edits the field order on a copy of the applicant form: pick a
 * service, open a step the way the applicant would, and drag the fields into
 * place. Empty spaces can be put between fields. Every step writes into one
 * list, which is stored only on Save.
 *
 * The documents keep their own order (DOCUMENT_DISPLAY_ORDER); the Documents
 * step and any step's documents are left out unless
 * FIELD_SEQUENCE_SHOW_DOCUMENTS_STEP is on, and then shown locked. The NOC
 * step is left out unless FIELD_SEQUENCE_SHOW_NOC_STEP is on.
 */
export default function FieldSequenceEditor() {
  const { data: adminView, isLoading, isError } = useFieldSequenceAdminView();
  const { mutate: saveSequence, isPending } = useSaveFieldSequence();

  /**
   * The services listed are those of the Name of Service field (the same
   * list the applicant picks from) that have an applicant form.
   */
  const { data: nameOfService } = useNameOfServiceField();
  const services = useMemo(() => {
    const withForm = new Set((adminView?.services ?? []).map((name) => name.toLowerCase()));
    return (nameOfService?.options ?? []).filter((option) =>
      withForm.has(String(option.name).toLowerCase())
    );
  }, [nameOfService, adminView]);

  const [serviceId, setServiceId] = useState<number>();
  useEffect(() => {
    if (serviceId !== undefined || !services.length) return;
    const preferred = services.find(
      (option) => String(option.name).toLowerCase() === DEFAULT_SERVICE_NAME
    );
    setServiceId(Number((preferred ?? services[0]).id));
  }, [services, serviceId]);

  const { data: stepsResult, isFetching: isFetchingSteps } = useApplicationFormSteps(serviceId);
  const steps: TPopulatedForm[] = useMemo(
    () =>
      (stepsResult?.data ?? [])
        .filter((step) => branchFeatureFlags.FIELD_SEQUENCE_SHOW_NOC_STEP || step.type !== "NOC")
        .filter(
          (step) => branchFeatureFlags.FIELD_SEQUENCE_SHOW_DOCUMENTS_STEP || !!step.fields?.length
        ),
    [stepsResult]
  );
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => setActiveStep(0), [serviceId]);

  /** The saved order, completed with every field it does not mention yet. */
  const savedOrder = useMemo(
    () => (adminView ? buildOrder(adminView.fields, adminView.savedItems) : []),
    [adminView]
  );
  const [order, setOrder] = useState<TOrderItem[]>([]);
  /**
   * Whether the working list has taken its first copy from the server. Until
   * it has, the empty starting list is not a change the Admin made.
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const signature = (items: TOrderItem[]) => JSON.stringify(orderToSaved(items));
  const isDirty = isLoaded && signature(order) !== signature(savedOrder);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  /**
   * The list takes the first copy from the server as soon as it arrives, and
   * each later copy only while the Admin has no unsaved changes, so a refresh
   * never wipes work in progress.
   */
  useEffect(() => {
    if (!adminView) return;
    if (!isLoaded || !isDirtyRef.current) {
      setOrder(savedOrder);
      setIsLoaded(true);
    }
  }, [savedOrder, adminView, isLoaded]);

  const onReset = () => setOrder(buildOrder(adminView?.fields ?? [], []));
  const onCancel = () => setOrder(savedOrder);
  const onSave = () =>
    saveSequence(
      { items: orderToSaved(order) },
      { onSuccess: () => SuccessToast("Field sequence saved") }
    );

  if (isLoading) return <p className="p-5 text-left text-gray-500">Loading the fields…</p>;
  if (isError || !adminView) {
    return <p className="p-5 text-left text-gray-500">The fields could not be loaded.</p>;
  }

  const step = steps[Math.min(activeStep, Math.max(steps.length - 1, 0))];

  return (
    <div className="p-3 sm:p-5 text-left">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <Select
          label="Name of Service"
          containerClass="w-full sm:w-[320px]"
          value={serviceId ?? ""}
          onChange={(e) => setServiceId(Number(e.target.value))}
        >
          {services.map((service) => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </Select>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onReset}
            disabled={isPending}
            className="border-2 border-primary text-primary px-6 py-2 rounded-full font-bold text-sm disabled:opacity-50"
          >
            Reset to default
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={!isDirty || isPending}
            className="bg-black px-6 py-2 rounded-full font-bold text-white text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!isDirty || isPending}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <p className="text-sm mb-6 text-gray-500">
        {isDirty
          ? "You have changes that are not saved yet. They apply to every service that shares these fields."
          : "Drag a field to its place, or use the arrows on its badge. Changes are stored when you click Save."}
      </p>

      {!steps.length ? (
        <p className="text-gray-500">
          {isFetchingSteps ? "Loading the form…" : "This service has no form steps."}
        </p>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-300 p-4 sm:p-8">
          <p className="text-center mb-8 text-grey-600 text-2xl font-bold">Application Details</p>
          <FormStepper steps={steps} activeStep={activeStep} onSelect={setActiveStep} />
          {step && (
            <StepEditor
              key={`${serviceId}-${step.id}`}
              step={step}
              order={order}
              savedItems={adminView.savedItems}
              onChange={setOrder}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** The step circles of the applicant form; a step opens on click. */
function FormStepper({
  steps,
  activeStep,
  onSelect,
}: {
  steps: TPopulatedForm[];
  activeStep: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="flex justify-start sm:justify-center mb-8 max-w-[650px] mx-auto overflow-x-auto pb-2">
      {steps.map((step, i) => (
        <Fragment key={`step-${step.id}`}>
          <button
            type="button"
            className="flex flex-col items-center min-w-[70px]"
            onClick={() => onSelect(i)}
          >
            <div
              className={`w-[20px] h-[20px] box-content p-1 rounded-full border-4 ${
                i <= activeStep ? "border-primary" : "border-gray-400"
              }`}
            >
              <div
                className={`w-[20px] box-content h-[20px] rounded-full ${
                  i <= activeStep ? "bg-primary" : "bg-gray-400"
                }`}
              ></div>
            </div>
            <p className="text-base">{`Step ${i + 1}`}</p>
            <p className="text-xs text-gray-500 text-center">{stepTitle(step)}</p>
          </button>
          {i !== steps.length - 1 && (
            <hr
              className={`grow min-w-[16px] mt-[16px] border-4 ${i < activeStep ? "border-primary" : ""}`}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}

/** A short title for a step: its form name, or "Documents" for a documents-only step. */
function stepTitle(step: TPopulatedForm) {
  if (!step.fields?.length && step.documentConfigs?.length) return "Documents";
  return step.name || step.type;
}

/**
 * Plays a short slide for every card whose place changed, and rings the cards
 * that moved, so the Admin sees what was swapped. `capture` is called just
 * before the order changes; the slide runs once the new order is drawn.
 */
function useReorderAnimation(itemKeys: string[]) {
  const nodes = useRef(new Map<string, HTMLDivElement>());
  const before = useRef<Map<string, DOMRect> | null>(null);
  const [movedKeys, setMovedKeys] = useState<Set<string>>(new Set());
  const keySignature = itemKeys.join("|");

  const capture = () => {
    const rects = new Map<string, DOMRect>();
    nodes.current.forEach((el, key) => rects.set(key, el.getBoundingClientRect()));
    before.current = rects;
  };

  useLayoutEffect(() => {
    const previous = before.current;
    if (!previous) return;
    before.current = null;

    const moved = new Set<string>();
    nodes.current.forEach((el, key) => {
      const from = previous.get(key);
      if (!from) return;
      const to = el.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (!dx && !dy) return;
      moved.add(key);
      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        el.style.transition = "transform 200ms ease-out";
        el.style.transform = "";
      });
    });
    if (!moved.size) return;
    setMovedKeys(moved);
    const timer = window.setTimeout(() => setMovedKeys(new Set()), 700);
    return () => window.clearTimeout(timer);
  }, [keySignature]);

  const refFor = (key: string) => (el: HTMLDivElement | null) => {
    if (el) nodes.current.set(key, el);
    else nodes.current.delete(key);
  };

  return { capture, refFor, movedKeys };
}

/**
 * One step of the form, drawn with the applicant form's own fields in its
 * two-column grid. Each field (and each empty space) is dragged into place,
 * or moved with the arrows on its badge. The documents are listed locked.
 */
function StepEditor({
  step,
  order,
  savedItems,
  onChange,
}: {
  step: TPopulatedForm;
  order: TOrderItem[];
  savedItems: TSavedItem[];
  onChange: (order: TOrderItem[]) => void;
}) {
  /** The fields are drawn by RenderField, which reads its values from a form. */
  const formMethods = useForm();

  const fieldsById = useMemo(
    () => new Map((step.fields ?? []).map((field) => [Number(field.id), field])),
    [step]
  );
  /** The step's fields the form draws (Main Khasara No. is never drawn on its own). */
  const stepFieldIds = useMemo(
    () =>
      new Set(
        defaultFieldOrder(
          (step.fields ?? []).map((field) => ({ id: Number(field.id), uid: field.uid }))
        ).map((field) => field.id)
      ),
    [step]
  );
  const items = stepItems(order, stepFieldIds, step.id);
  const documents = branchFeatureFlags.FIELD_SEQUENCE_SHOW_DOCUMENTS_STEP
    ? sortByDocumentDisplayOrder(step.documentConfigs ?? [], (doc) => doc.document_config_id)
    : [];

  const savedFieldIds = useMemo(
    () => new Set(savedItems.flatMap((item) => ("fieldId" in item ? [item.fieldId] : []))),
    [savedItems]
  );
  const isNewField = (item: TOrderItem) =>
    item.kind === "field" && savedItems.length > 0 && !savedFieldIds.has(item.fieldId);

  const { capture, refFor, movedKeys } = useReorderAnimation(items.map((item) => item.key));

  const applyItems = (next: TOrderItem[]) => {
    capture();
    onChange(placeStepItems(order, stepFieldIds, step.id, next));
  };

  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const finishDrag = (to: number | null) => {
    if (dragFrom !== null && to !== null && dragFrom !== to) {
      applyItems(moveItem(items, dragFrom, to));
    }
    setDragFrom(null);
    setDragOver(null);
  };

  /**
   * Touch screens do not fire the browser's drag events, so on a phone a
   * card is dragged by the ⠿ handle on its badge with touch events: the card
   * under the finger is found from its data-row-index, and the move happens
   * when the finger lifts. The handle has touch-action: none so the page does
   * not scroll while dragging; touching anywhere else still scrolls.
   */
  const rowIndexAt = (touch: { clientX: number; clientY: number }) => {
    const card = document
      .elementFromPoint(touch.clientX, touch.clientY)
      ?.closest("[data-row-index]");
    const index = card ? Number(card.getAttribute("data-row-index")) : NaN;
    return Number.isNaN(index) ? null : index;
  };

  const addSpaceAfter = (index: number) => {
    const next = [...items];
    next.splice(index + 1, 0, newSpacer(step.id));
    applyItems(next);
  };

  const removeItem = (index: number) => applyItems(items.filter((_, i) => i !== index));

  return (
    <FormProvider {...formMethods}>
      <div className="max-w-[1000px] mx-auto">
        {!!items.length && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-10">
            {items.map((item, index) => {
              const field = item.kind === "field" ? fieldsById.get(item.fieldId) : undefined;
              if (item.kind === "field" && !field) return null;
              const label = field?.label ?? "Empty space";
              const isTarget = dragOver === index && dragFrom !== index;

              return (
                <div
                  key={item.key}
                  ref={refFor(item.key)}
                  data-row-index={index}
                  className={[
                    "relative flex flex-col rounded-lg transition-shadow duration-300",
                    movedKeys.has(item.key) ? "ring-2 ring-primary ring-offset-4" : "",
                    dragFrom === index ? "opacity-40" : "",
                  ].join(" ")}
                  onDragOver={(e: DragEvent) => {
                    e.preventDefault();
                    setDragOver(index);
                  }}
                  onDragLeave={() => setDragOver((current) => (current === index ? null : current))}
                  onDrop={(e: DragEvent) => {
                    e.preventDefault();
                    finishDrag(index);
                  }}
                >
                  {item.kind === "spacer" ? (
                    <div className="h-[84px] rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-400">
                      Empty space
                    </div>
                  ) : field && CONSTRUCTION_ONLY_UIDS.includes(field.uid) ? (
                    <ConstructionOnlyField label={field.label} />
                  ) : (
                    <div className="pointer-events-none" aria-hidden="true">
                      {field && <RenderField field={field} />}
                    </div>
                  )}

                  {/* The whole card is the drag surface; the form inside it is not usable here. */}
                  <div
                    draggable
                    onDragStart={() => setDragFrom(index)}
                    onDragEnd={() => {
                      setDragFrom(null);
                      setDragOver(null);
                    }}
                    className="absolute inset-0 cursor-grab active:cursor-grabbing"
                    title={`Drag ${label}`}
                  />

                  {isTarget && (
                    <div className="absolute inset-0 rounded-lg border-2 border-dashed border-primary pointer-events-none" />
                  )}

                  {/* The badge has its own row above the field (order-first), right-aligned,
                      so it never covers the field's label on any screen width. */}
                  <div className="relative z-10 order-first self-end mb-1 flex flex-wrap items-center justify-end gap-1 bg-white border border-gray-300 rounded-full px-2 py-0.5 shadow-sm max-w-full">
                    <span
                      className="text-gray-400 text-lg leading-none px-1 cursor-grab"
                      style={{ touchAction: "none" }}
                      aria-hidden="true"
                      onTouchStart={() => setDragFrom(index)}
                      onTouchMove={(e) => {
                        if (dragFrom === null) return;
                        setDragOver(rowIndexAt(e.touches[0]));
                      }}
                      onTouchEnd={(e) => finishDrag(rowIndexAt(e.changedTouches[0]))}
                      onTouchCancel={() => finishDrag(null)}
                    >
                      ⠿
                    </span>
                    {/* The position is typed in: Enter or leaving the box moves
                        the field straight there (1 = first), instead of one
                        place per arrow click. The key resets the box to the
                        field's new position after every move. */}
                    <input
                      key={`${item.key}-${index}`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={items.length}
                      defaultValue={index + 1}
                      aria-label={`Position of ${label}`}
                      title="Type a position and press Enter"
                      className="w-9 h-6 px-0 rounded-full bg-transparent border-0 text-center text-xs font-bold text-primary hover:bg-gray-100 focus:bg-amber-50 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      onFocus={(e) => e.currentTarget.select()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") {
                          e.currentTarget.value = String(index + 1);
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        const position = Number(e.currentTarget.value);
                        if (!Number.isInteger(position) || position < 1) {
                          e.currentTarget.value = String(index + 1);
                          return;
                        }
                        if (position - 1 !== index) {
                          applyItems(moveItem(items, index, position - 1));
                        }
                      }}
                    />
                    {isNewField(item) && (
                      <span className="text-[10px] text-blue-700 bg-blue-50 rounded-full px-1.5">New</span>
                    )}
                    <BadgeButton
                      label={`Move ${label} up`}
                      disabled={index === 0}
                      onClick={() => applyItems(moveItem(items, index, index - 1))}
                    >
                      ▲
                    </BadgeButton>
                    <BadgeButton
                      label={`Move ${label} down`}
                      disabled={index === items.length - 1}
                      onClick={() => applyItems(moveItem(items, index, index + 1))}
                    >
                      ▼
                    </BadgeButton>
                    {item.kind === "spacer" ? (
                      <BadgeButton label="Remove this empty space" onClick={() => removeItem(index)}>
                        ✕
                      </BadgeButton>
                    ) : (
                      <BadgeButton
                        label={`Add an empty space after ${label}`}
                        onClick={() => addSpaceAfter(index)}
                      >
                        ＋
                      </BadgeButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!!documents.length && (
          <div className="mt-8">
            <p className="text-sm text-gray-500 mb-3">
              🔒 Documents are shown in their current order. Ordering documents comes in the next phase.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
              {documents.map((doc) => (
                <div key={`doc-${doc.document_config_id}`} className="text-left opacity-70">
                  <label className="mb-2 font-semibold block">
                    {doc.document_label}
                    {!!doc.mandatory && <span className="text-red-500"> *</span>}
                  </label>
                  <div className="flex items-center gap-3 w-full h-12 px-4 rounded-lg border border-gray-400 bg-gray-200">
                    <span aria-hidden="true">🔒</span>
                    <span className="grow text-sm text-gray-400">Choose file</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!items.length && !documents.length && (
          <p className="text-gray-500">This step has nothing to order.</p>
        )}
      </div>
    </FormProvider>
  );
}

/** A small round button on a card's badge. */
function BadgeButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="w-6 h-6 rounded-full text-[11px] text-gray-600 hover:bg-gray-100 disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/**
 * Construction Area is drawn by the form only after a Yes to "Is there a
 * construction?", so here it is shown as the same input shape with that note.
 */
function ConstructionOnlyField({ label }: { label: string }) {
  return (
    <div className="pointer-events-none" aria-hidden="true">
      <label className="mb-2 font-semibold block">{label}</label>
      <div className="flex items-center w-full h-12 px-4 rounded-lg border border-dashed border-gray-400 bg-white">
        <span className="text-sm text-gray-400">Shown only when there is a construction</span>
      </div>
    </div>
  );
}
